// The boxes that move money or send: receipt, send, collect a payment, edit lines, charge the card, refund.
import { chargeQuestion, type InvoiceDetail } from "@steward/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTestMode } from "../../api/hooks";
import {
  type InvoiceLineIn,
  newKey,
  useChargeCard,
  useEditPaid,
  useRecordPayment,
  useRefund,
  useSendInvoice,
  useSendPreview,
  useSendReceipt,
  useUpdateInvoice,
  type Via,
} from "../../api/money";
import { useSession } from "../../app/session";
import { Choice, InputText } from "../../components/fields";
import { moneyExact } from "../../components/format";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, LoadError, Skeleton } from "../../components/parts";
import { draftLine, type DraftLine, draftTotal, errText, LinesEditor, linesOut } from "../../components/work";
import { PhoneCard } from "../PhoneCard";
import { dollars } from "./common";

/** Send receipt (paid invoices): a text, or an email with the receipt PDF, to the client's own number or email. */
export function ReceiptBox({ i, onClose }: { i: InvoiceDetail; onClose: () => void }) {
  const [via, setVia] = useState<Via>(i.client.email ? "email" : "text");
  const [picked, setPicked] = useState<string | null>(null);
  const preview = useSendPreview(i.id, via, true);
  const receipt = useSendReceipt();
  const toast = useToast();
  const testMode = useTestMode(useSession().company.id);
  const choices = preview.data?.choices ?? [];
  const to = picked && choices.includes(picked) ? picked : (choices[0] ?? null);
  return (
    <Modal
      title="Send receipt"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="mail"
            disabled={!to}
            loading={receipt.isPending}
            onClick={() =>
              to &&
              receipt.mutate(
                { id: i.id, via, to },
                {
                  onSuccess: () => {
                    toast(testMode ? `Receipt saved for ${to}. Test mode, so it waits in the outbox.` : `Receipt sent to ${to}.`);
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Send receipt
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setPicked(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email, with the PDF" },
          ]}
        />
        {choices.length > 1 ? (
          <Choice<string>
            label="Send to"
            value={to ?? ""}
            onChange={setPicked}
            options={choices.map((c) => ({ value: c, label: c }))}
          />
        ) : (
          <div className="field">
            <span className="label">Send to</span>
            <b>{to ?? `${i.client.name} has no ${via === "text" ? "phone number" : "email"}.`}</b>
          </div>
        )}
        <p className="muted small">It says the amount, the day it was paid and how, with the link to the invoice.</p>
      </div>
    </Modal>
  );
}

export function SendBox({ i, via: firstVia, open, onClose }: { i: InvoiceDetail; via: Via; open: boolean; onClose: () => void }) {
  const [via, setVia] = useState<Via>(firstVia);
  const [picked, setPicked] = useState<string | null>(null);
  const [later, setLater] = useState("");
  const [cc, setCc] = useState<string[]>([]);
  const [copyMe, setCopyMe] = useState(false);
  const [pdf, setPdf] = useState<boolean | null>(null);
  const testMode = useTestMode(useSession().company.id);
  const preview = useSendPreview(i.id, via, open);
  const send = useSendInvoice();
  const toast = useToast();
  const p = preview.data;
  const to = picked && p?.choices.includes(picked) ? picked : (p?.to ?? null);
  const go = () => {
    if (!to) return;
    send.mutate(
      {
        id: i.id,
        via,
        to,
        ...(later ? { sendAt: new Date(later).toISOString() } : {}),
        cc: cc.filter((x) => x !== to),
        copyMe,
        pdf: pdf ?? p?.pdf ?? false,
      },
      {
        onSuccess: () => {
          toast(
            later
              ? `Invoice will go to ${to} at the time you picked.`
              : testMode
                ? `Invoice saved for ${to}. Test mode, so it waits in the outbox.`
                : `Invoice sent to ${to}.`,
          );
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  };
  return (
    <Modal
      title={i.state === "to_check" ? "Confirm and send" : "Resend invoice"}
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="mail" disabled={!to} loading={send.isPending} onClick={go}>
            Send {via === "text" ? "text" : "email"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setPicked(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email" },
          ]}
        />
        {preview.error ? <LoadError error={preview.error} /> : null}
        {p ? (
          <>
            {p.choices.length > 1 ? (
              <Choice<string>
                label="Send to"
                value={to ?? ""}
                onChange={setPicked}
                options={p.choices.map((c) => ({ value: c, label: c }))}
              />
            ) : (
              <div className="field">
                <span className="label">Send to</span>
                <b>
                  {to ?? `${i.client.name} has no ${via === "text" ? "phone number" : "email"}. Add one on the client first.`}
                </b>
              </div>
            )}
            {p.warnings.map((w) => (
              <Banner key={w} type="warning">
                {w}
              </Banner>
            ))}
            {p.subject ? (
              <div className="field">
                <span className="label">Subject</span>
                <span>{p.subject}</span>
              </div>
            ) : null}
            <div className="field">
              <span className="label">The message</span>
              <div className="message-preview">{p.body}</div>
            </div>
            {via === "email" ? (
              <div className="stack">
                {p.choices
                  .filter((c) => c !== to)
                  .map((c) => (
                    <label key={c} className="check-row">
                      <input
                        type="checkbox"
                        checked={cc.includes(c)}
                        onChange={(e) => setCc(e.target.checked ? [...cc, c] : cc.filter((x) => x !== c))}
                      />
                      <span>Copy to {c}</span>
                    </label>
                  ))}
                <label className="check-row">
                  <input type="checkbox" checked={pdf ?? p.pdf} onChange={(e) => setPdf(e.target.checked)} />
                  <span>Attach a PDF copy</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={copyMe} onChange={(e) => setCopyMe(e.target.checked)} />
                  <span>Send me a copy</span>
                </label>
              </div>
            ) : null}
            {testMode ? (
              <p className="muted small">Texts and emails are in test mode. This goes to the outbox and is not sent.</p>
            ) : null}
            {i.status === "to_check" || i.status === "scheduled" ? (
              <InputText
                label="Send later (leave empty to send now)"
                type="datetime-local"
                value={later}
                onChange={(e) => setLater(e.target.value)}
              />
            ) : null}
          </>
        ) : (
          <Skeleton rows={3} />
        )}
      </div>
    </Modal>
  );
}

/**
 * Cash, check or card (owner, Nov 1: "also add manual adding card for a customer in case they have no payment saved").
 * Card charges the balance on the saved card, after naming the client, card and amount. With no saved card the office
 * types the one the client reads out (Take card by phone, saved like theirs), then charges it here.
 */
export function CollectPayment({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const record = useRecordPayment();
  const charge = useChargeCard();
  const toast = useToast();
  const [method, setMethod] = useState<"cash" | "check" | "card">(
    i.crewCollected === "check" || i.crewCollected === "company" ? "check" : "cash",
  );
  const [amount, setAmount] = useState("");
  const [check, setCheck] = useState("");
  const [key, setKey] = useState(newKey);
  const [taking, setTaking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const card = i.client.card;
  const mayCard = can("card.charge") && i.balanceCents >= 50;
  const shown = amount || (i.balanceCents / 100).toFixed(2);
  const done = (words: string) => {
    toast(words);
    setKey(newKey());
    setAmount("");
    setCheck("");
    setErr(null);
    onClose();
  };
  const go = () => {
    if (method === "card") {
      if (!card) {
        setTaking(true);
        return;
      }
      charge.mutate(
        { id: i.id, key, amountCents: i.balanceCents, last4: card.last4 },
        { onSuccess: () => done(`${moneyExact(i.balanceCents)} charged`), onError: (e) => setErr(errText(e)) },
      );
      return;
    }
    const cents = dollars(shown);
    if (cents === null) {
      setErr("Type the amount you got.");
      return;
    }
    if (cents > i.balanceCents) {
      setErr(`That is more than the balance of ${moneyExact(i.balanceCents)}.`);
      return;
    }
    record.mutate(
      { id: i.id, key, method, amountCents: cents, checkNumber: method === "check" ? check.trim() || null : null },
      { onSuccess: () => done(`${moneyExact(cents)} ${method} recorded`), onError: (e) => setErr(errText(e)) },
    );
  };
  if (open && taking)
    return (
      <PhoneCard
        c={i.client}
        onClose={() => setTaking(false)}
        onSaved={() => {
          // The invoice shows the new card, then Charge asks before any money moves.
          void qc.invalidateQueries({ queryKey: ["invoice", i.id] }).then(() => setTaking(false));
        }}
      />
    );
  return (
    <Modal
      title="Collect payment"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={record.isPending || charge.isPending} onClick={go}>
            {method === "card" ? (card ? `Yes, charge ${moneyExact(i.balanceCents)}` : "Add card") : "Record payment"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<"cash" | "check" | "card">
          label="Paid by"
          value={method}
          onChange={(m) => {
            setMethod(m);
            setErr(null);
          }}
          options={[
            { value: "cash", label: "Cash" },
            { value: "check", label: "Check" },
            ...(mayCard ? [{ value: "card" as const, label: "Card" }] : []),
          ]}
        />
        {method === "card" ? (
          card ? (
            <>
              <p>
                <b>
                  {chargeQuestion({
                    client: i.client.name,
                    cardBrand: card.brand,
                    last4: card.last4,
                    amountCents: i.balanceCents,
                  })}
                </b>
              </p>
              <p className="muted small">
                The card is charged right away for the whole balance. Wrong card?{" "}
                <button type="button" className="linkish" onClick={() => setTaking(true)}>
                  Add a different card
                </button>
              </p>
            </>
          ) : (
            <p className="muted small">
              {i.client.name} has no saved card. Add card opens a secure Stripe box. Type the card as they read it to you. It is
              saved, then you charge {moneyExact(i.balanceCents)} here.
            </p>
          )
        ) : (
          <>
            <InputText
              label="Amount"
              prefix="$"
              inputMode="decimal"
              value={shown}
              error={err}
              onChange={(e) => setAmount(e.target.value)}
            />
            {method === "check" ? (
              <InputText label="Check number" value={check} onChange={(e) => setCheck(e.target.value)} />
            ) : null}
            {mayCard ? null : <p className="muted small">Card payments come in by themselves from the pay page.</p>}
          </>
        )}
        {method === "card" && err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

/** Changes the lines. On a paid invoice, a higher total opens it for the difference; a lower one asks Credit or Don't credit. */
export function EditLines({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const [lines, setLines] = useState<DraftLine[]>(() =>
    i.lines.map((l) =>
      draftLine({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents, frequency: "every" }),
    ),
  );
  const [choice, setChoice] = useState<"credit" | "no_credit" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const update = useUpdateInvoice();
  const editPaid = useEditPaid();
  const toast = useToast();
  const paid = i.paidCents;
  const total = draftTotal(lines) + i.tipCents;
  const lower = paid > 0 && total < paid;
  const go = () => {
    const out = linesOut(lines);
    if (typeof out === "string") {
      setErr(out);
      return;
    }
    if (!out.length) {
      setErr("An invoice needs a line.");
      return;
    }
    const body: InvoiceLineIn[] = out.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
    }));
    if (lower && !choice) {
      setErr("Pick Credit or Don't credit for the money already paid.");
      return;
    }
    const after = {
      onSuccess: () => {
        toast(
          lower
            ? choice === "credit"
              ? "Saved. The difference is now account credit."
              : "Saved. No credit given."
            : "Line items saved",
        );
        onClose();
      },
      onError: (e: unknown) => setErr(errText(e)),
    };
    if (paid > 0) editPaid.mutate({ id: i.id, body: { lines: body, ...(lower && choice ? { choice } : {}) } }, after);
    else update.mutate({ id: i.id, patch: { lines: body } }, after);
  };
  return (
    <Modal
      title="Edit line items"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={update.isPending || editPaid.isPending} onClick={go}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        {paid > 0 ? (
          <Banner type="notice">
            {moneyExact(paid)} is already paid. A higher total asks the client for the difference only.
          </Banner>
        ) : null}
        <LinesEditor lines={lines} onChange={setLines} />
        {lower ? (
          <Choice<"credit" | "no_credit">
            label={`The new total is ${moneyExact(paid - total)} less than what was paid`}
            value={choice ?? ("" as "credit")}
            onChange={setChoice}
            options={[
              { value: "credit", label: "Credit", hint: "Keep it as account credit for the next invoice" },
              { value: "no_credit", label: "Don't credit", hint: "Keep the money. A note is added" },
            ]}
          />
        ) : null}
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

/** Owner only. Names the client, card and amount, and asks before charging. */
export function ChargeCard({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const charge = useChargeCard();
  const toast = useToast();
  const [key, setKey] = useState(newKey);
  const card = i.client.card;
  if (!card) return null;
  return (
    <Modal
      title="Charge saved card"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={charge.isPending}
            onClick={() =>
              charge.mutate(
                { id: i.id, key, amountCents: i.balanceCents, last4: card.last4 },
                {
                  onSuccess: () => {
                    toast(`${moneyExact(i.balanceCents)} charged`);
                    setKey(newKey());
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Yes, charge {moneyExact(i.balanceCents)}
          </Button>
        </>
      }
    >
      <p>
        <b>{chargeQuestion({ client: i.client.name, cardBrand: card.brand, last4: card.last4, amountCents: i.balanceCents })}</b>
      </p>
      <p className="muted">
        {i.status === "to_check"
          ? "The invoice gets its number and is marked paid. The receipt goes out like any card payment."
          : "The card is charged right away."}
      </p>
    </Modal>
  );
}

/** Give back part or all of a payment (owners only, question 33). */
export function RefundBox({ i, paymentId, onClose }: { i: InvoiceDetail; paymentId: string; onClose: () => void }) {
  const refund = useRefund();
  const toast = useToast();
  const p = i.payments.find((x) => x.id === paymentId);
  const [amount, setAmount] = useState(p ? (p.amountCents / 100).toFixed(2) : "");
  const [reason, setReason] = useState("");
  const [keepOwed, setKeepOwed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = Math.round(Number(amount.replace(/[$,\s]/g, "")) * 100);
  return (
    <Modal
      title="Refund"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={refund.isPending}
            disabled={!(cents > 0) || !reason.trim()}
            onClick={() =>
              refund.mutate(
                { id: i.id, paymentId, amountCents: cents, reason: reason.trim(), keepOwed },
                {
                  onSuccess: () => {
                    toast("Refund made");
                    onClose();
                  },
                  onError: (e) => setError(e instanceof Error ? e.message : "That did not go through."),
                },
              )
            }
          >
            Give back {cents > 0 ? moneyExact(cents) : ""}
          </Button>
        </>
      }
    >
      <p className="muted">
        {p?.method === "card" ? "The money goes back on the client's card." : "Write down cash or a check you gave back."}
      </p>
      <InputText label="Amount" prefix="$" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <InputText label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
      <label className="check-row">
        <input type="checkbox" checked={keepOwed} onChange={(e) => setKeepOwed(e.target.checked)} />
        They still owe it (a payment made by mistake). Leave off to lower the invoice too.
      </label>
      {error ? <Banner type="error">{error}</Banner> : null}
    </Modal>
  );
}
